/**
 * Static completion entries for manual SCIM filter editing (Ace completer).
 */

export interface ScimAceStaticCompletion {
    caption: string;
    value?: string;
    meta?: string;
    score?: number;
}

const SCIM_FILTER_KEYWORDS: ScimAceStaticCompletion[] = [
    { caption: 'eq', meta: 'operator', score: 50 },
    { caption: 'ne', meta: 'operator', score: 50 },
    { caption: 'co', meta: 'operator', score: 50 },
    { caption: 'sw', meta: 'operator', score: 50 },
    { caption: 'ew', meta: 'operator', score: 50 },
    { caption: 'pr', meta: 'operator', score: 50 },
    { caption: 'gt', meta: 'operator', score: 40 },
    { caption: 'lt', meta: 'operator', score: 40 },
    { caption: 'ge', meta: 'operator', score: 40 },
    { caption: 'le', meta: 'operator', score: 40 },
    { caption: 'and', meta: 'keyword', score: 30 },
    { caption: 'or', meta: 'keyword', score: 30 },
    { caption: 'not', meta: 'keyword', score: 30 },
    { caption: 'present', meta: 'operator', score: 40 },
    { caption: 'absent', meta: 'operator', score: 40 },
    { caption: 'true', meta: 'literal', score: 20 },
    { caption: 'false', meta: 'literal', score: 20 },
    { caption: 'null', meta: 'literal', score: 20 },
];

/**
 * Custom `$…` operators and functions exercised by ScimParse (see `Library.fs` filter examples ~538–581).
 * Parser allows `$Name[json,…]` with optional `.subOp` (e.g. `$daysago[3].gt`) and `$prefix.subOp` (e.g. `$cnt.ge`).
 */
const SCIM_CUSTOM_FILTER_OPERATORS: ScimAceStaticCompletion[] = [
    { caption: '$In', value: '$In ', meta: 'custom operator', score: 62 },
    { caption: '$cnt.ge', value: '$cnt.ge ', meta: '$cnt', score: 58 },
    { caption: '$cnt.gte', value: '$cnt.gte ', meta: '$cnt', score: 58 },
    { caption: '$cnt.lt', value: '$cnt.lt ', meta: '$cnt', score: 58 },
    { caption: '$cnt.le', value: '$cnt.le ', meta: '$cnt', score: 58 },
    { caption: '$cnt.eq', value: '$cnt.eq ', meta: '$cnt', score: 58 },
    { caption: '$cnt.ne', value: '$cnt.ne ', meta: '$cnt', score: 58 },
    { caption: '$cnt[args].pr', value: '$cnt[].pr', meta: '$cnt + JSON args', score: 55 },
    { caption: '$cnt[args]', value: '$cnt[', meta: '$cnt + JSON args', score: 54 },
    { caption: '$daysago[args].gt', value: '$daysago[3].gt ', meta: 'custom function', score: 55 },
    { caption: '$daysago[args]', value: '$daysago[', meta: 'custom function', score: 54 },
    { caption: '$daysago.gt', value: '$daysago.gt ', meta: 'custom function', score: 54 },
    { caption: '$filter[args].co', value: '$filter[].co ', meta: 'custom function', score: 55 },
    { caption: '$filter[args]', value: '$filter[', meta: 'custom function', score: 54 },
    { caption: '$avg[args].ge', value: '$avg[].ge ', meta: 'custom function', score: 55 },
    { caption: '$avg[args]', value: '$avg[', meta: 'custom function', score: 54 },
    { caption: '$func[args].eq', value: '$func[].eq ', meta: 'custom function', score: 52 },
    { caption: '$func[args]', value: '$func[', meta: 'custom function', score: 51 },
];

/** Merge tier2-searchable attribute names with common SCIM filter tokens for editor autocomplete. */
export function buildScimFilterEditorCompletions(attributeNames: string[]): ScimAceStaticCompletion[] {
    const seen = new Set<string>();
    const attrs: ScimAceStaticCompletion[] = [];
    for (const raw of attributeNames) {
        const name = String(raw || '').trim();
        if (!name) continue;
        const key = name.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        attrs.push({ caption: name, value: name, meta: 'attribute', score: 100 });
    }
    return [...attrs, ...SCIM_CUSTOM_FILTER_OPERATORS, ...SCIM_FILTER_KEYWORDS];
}
